import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';

interface ErrorGroupDetails {
  id: string;
  fingerprint: string;
  error_type: string;
  message: string;
  culprit?: string;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  user_count: number;
  status: string;
  priority?: string;
  assigned_to?: string;
  environment: string;
  release?: string;
  is_regression: boolean;
}

interface ErrorOccurrence {
  id: string;
  timestamp: string;
  user_id?: string;
  browser: string;
  os: string;
  url: string;
  stack_trace?: string;
  breadcrumbs?: any[];
}

export default function ErrorGroupDetailsPage() {
  const { t } = useTranslation('errors');
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [errorGroup, setErrorGroup] = useState<ErrorGroupDetails | null>(null);
  const [occurrences, setOccurrences] = useState<ErrorOccurrence[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'occurrences' | 'users' | 'trends' | 'comments'>('overview');

  useEffect(() => {
    if (groupId) {
      loadErrorGroupData();
    }
  }, [groupId]);

  const loadErrorGroupData = async () => {
    try {
      setLoading(true);
      // TODO: Implement API calls
      // const [groupData, occurrencesData] = await Promise.all([
      //   errorTrackingService.getErrorGroup(groupId),
      //   errorTrackingService.getErrorOccurrences(groupId)
      // ]);

      // Mock data
      setErrorGroup({
        id: groupId!,
        fingerprint: 'abc123def456',
        error_type: 'TypeError',
        message: 'Cannot read property \'length\' of undefined',
        culprit: 'app.js in processData',
        first_seen_at: new Date(Date.now() - 86400000 * 5).toISOString(),
        last_seen_at: new Date().toISOString(),
        occurrence_count: 245,
        user_count: 89,
        status: 'unresolved',
        priority: 'high',
        environment: 'production',
        release: 'v2.5.1',
        is_regression: false,
      });

      setOccurrences([
        {
          id: '1',
          timestamp: new Date().toISOString(),
          user_id: 'user_123',
          browser: 'Chrome 119.0',
          os: 'Windows 10',
          url: 'https://example.com/dashboard',
          stack_trace: `TypeError: Cannot read property 'length' of undefined
    at processData (app.js:42:15)
    at handleClick (app.js:128:5)
    at HTMLButtonElement.<anonymous> (app.js:256:9)`,
          breadcrumbs: [
            { type: 'navigation', message: 'User navigated to /dashboard' },
            { type: 'click', message: 'Clicked button#submit' },
          ],
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          browser: 'Chrome 118.0',
          os: 'macOS 14',
          url: 'https://example.com/dashboard',
          stack_trace: `TypeError: Cannot read property 'length' of undefined
    at processData (app.js:42:15)
    at handleClick (app.js:128:5)`,
        },
      ]);
    } catch (error) {
      console.error('Failed to load error group:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    try {
      // TODO: Implement API call
      // await errorTrackingService.resolveError(groupId);
      console.log('Resolving error:', groupId);
      loadErrorGroupData();
    } catch (error) {
      console.error('Failed to resolve error:', error);
    }
  };

  const handleIgnore = async () => {
    try {
      // TODO: Implement API call
      // await errorTrackingService.ignoreError(groupId);
      console.log('Ignoring error:', groupId);
      loadErrorGroupData();
    } catch (error) {
      console.error('Failed to ignore error:', error);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'unresolved':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ignored':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority?: string): string => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!errorGroup) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">Error group not found</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/analytics/rum')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {errorGroup.error_type}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {errorGroup.message}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {errorGroup.status === 'unresolved' && (
              <>
                <button
                  onClick={handleResolve}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('details.actions.resolve')}
                </button>
                <button
                  onClick={handleIgnore}
                  className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {t('details.actions.ignore')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'overview'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('details.tabs.overview')}
          </button>
          <button
            onClick={() => setActiveTab('occurrences')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'occurrences'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('details.tabs.occurrences')}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('details.tabs.users')}
          </button>
          <button
            onClick={() => setActiveTab('trends')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'trends'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('details.tabs.trends')}
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Error Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Error Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.fingerprint')}</p>
                  <code className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                    {errorGroup.fingerprint}
                  </code>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.errorType')}</p>
                  <p className="mt-1 font-medium text-gray-900 dark:text-white">{errorGroup.error_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.occurrences')}</p>
                  <p className="mt-1 font-medium text-gray-900 dark:text-white">
                    {errorGroup.occurrence_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.affectedUsers')}</p>
                  <p className="mt-1 font-medium text-gray-900 dark:text-white">{errorGroup.user_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.firstSeen')}</p>
                  <p className="mt-1 text-gray-900 dark:text-white">{formatTimestamp(errorGroup.first_seen_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.lastSeen')}</p>
                  <p className="mt-1 text-gray-900 dark:text-white">{formatTimestamp(errorGroup.last_seen_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.status')}</p>
                  <span className={`inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(errorGroup.status)}`}>
                    {t(`status.${errorGroup.status}`)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.priority')}</p>
                  {errorGroup.priority && (
                    <span className={`inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(errorGroup.priority)}`}>
                      {t(`priority.${errorGroup.priority}`)}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.environment')}</p>
                  <p className="mt-1 font-medium text-gray-900 dark:text-white capitalize">{errorGroup.environment}</p>
                </div>
                {errorGroup.release && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.release')}</p>
                    <code className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{errorGroup.release}</code>
                  </div>
                )}
                {errorGroup.culprit && (
                  <div className="col-span-full">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.errorInfo.culprit')}</p>
                    <p className="mt-1 font-medium text-gray-900 dark:text-white">{errorGroup.culprit}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Stack Trace */}
            {occurrences[0]?.stack_trace && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('details.stackTrace.title')}
                </h2>
                <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto font-mono">
                  {occurrences[0].stack_trace}
                </pre>
              </div>
            )}

            {/* Breadcrumbs */}
            {occurrences[0]?.breadcrumbs && occurrences[0].breadcrumbs.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('details.breadcrumbs.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-4">{t('details.breadcrumbs.subtitle')}</p>
                <div className="space-y-2">
                  {occurrences[0].breadcrumbs.map((crumb, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-sm">
                      <span className="text-gray-400">{crumb.type}</span>
                      <span className="text-gray-900 dark:text-white">{crumb.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Occurrences Tab */}
        {activeTab === 'occurrences' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('details.occurrences.title')}
              </h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {occurrences.map((occurrence) => (
                <div key={occurrence.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                        <span>{formatTimestamp(occurrence.timestamp)}</span>
                        <span>{occurrence.browser}</span>
                        <span>{occurrence.os}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{occurrence.url}</p>
                      {occurrence.user_id && (
                        <p className="text-xs text-gray-500 mt-1">User: {occurrence.user_id}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('details.affectedUsers.title')}
            </h2>
            <p className="text-gray-500">{errorGroup.user_count} users affected by this error</p>
          </div>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('details.trends.title')}
            </h2>
            <p className="text-gray-500">Error trends and charts will be displayed here</p>
          </div>
        )}
    </div>
    </MainLayout>
  );
}
